import { Controller } from "stimulus"
import config from "../../js/siteConfig"
import { trigger, validateEmail, lang, escapeHTML, getLocale, offset } from "../../js/utils"

export default class extends Controller {
  static get targets() {
    return [
      "form",
      "amounts",
      "plans",
      "customAmount",
      "customAmountRadio",
      "customAmountInput",
      "button",
      "paypal",
      "message",
      "subscriptionInfo",
    ]
  }

  connect() {
    this.formTarget.submit = this.submit
    this.lastSelectedAmount = null

    this.ppScriptNode = null
    this.ppLocale = getLocale() === "it" ? "it_IT" : getLocale() === "hu" ? "hu_HU" : "en_US"
    this.ppPlans = config.PAYPAL_SUBSCRIPTION_PLANS
    this.ppIsLoading = false

    this.selectPaymentFrequency()
    this.setCustomAmount()
    this.selectPaymentType()

    setTimeout(() => {}, 500)
  }

  validateForm(e) {
    if (e) e.preventDefault()

    const formData = this.getFormData()

    if (
      formData.paymentFrequency &&
      ((formData.paymentFrequency === "once" && formData.amount) ||
        (formData.paymentFrequency === "monthly" && formData.plan_amount)) &&
      window.paypal
    ) {
      this.paypalTarget.classList.remove("disabled")
      this.buttonTarget.classList.remove("disabled")
    } else {
      this.paypalTarget.classList.add("disabled")
      this.buttonTarget.classList.add("disabled")
    }
  }

  onSubmit(e) {
    if (e) e.preventDefault()

    const formData = this.getFormData()

    if (!formData.paymentFrequency || !formData.amount || !formData.plan_amount) {
      let errorMessage = ""

      if (!formData.paymentFrequency) {
        errorMessage += (errorMessage ? "<br>" : "") + lang("donate_form").error_frequency_missing
      }

      if (
        (formData.paymentFrequency === "once" && !formData.amount) ||
        (formData.paymentFrequency === "monthly" && !formData.plan_amount)
      ) {
        errorMessage += (errorMessage ? "<br>" : "") + lang("donate_form").error_amount_missing
        if (formData.paymentFrequency === "once") this.formTarget.otherAmount.focus()
      }

      if (!formData.paymentMethod) {
        errorMessage += (errorMessage ? "<br>" : "") + lang("donate_form").error_payment_method_missing
      }

      trigger("snackbar:show", {
        message: errorMessage,
        autoHide: true,
        status: "error",
      })

      return false
    }

    this.validateForm()
  }

  getFormData() {
    const formData = Object.fromEntries(new FormData(this.formTarget))

    for (var n in formData) {
      if (formData.hasOwnProperty(n)) {
        formData[n] = escapeHTML(formData[n].trim())
      }
    }

    return formData
  }

  setCustomAmount(e) {
    if (e) e.preventDefault()

    const customAmount = this.customAmountInputTarget.value.trim()

    this.customAmountRadioTarget.value = customAmount

    if (customAmount) {
      if (!this.lastSelectedAmount) {
        this.formTarget.querySelectorAll('input[name="amount"]').forEach((el) => {
          if (el !== this.customAmountRadioTarget && el.checked) this.lastSelectedAmount = el
        })
      }
      this.customAmountRadioTarget.checked = true
    } else if (this.lastSelectedAmount) {
      this.lastSelectedAmount.checked = true
    }

    this.customAmountTarget.classList.toggle("is-filled", customAmount)
  }

  unsetCustomAmount(e) {
    if (e) e.preventDefault()

    this.customAmountInputTarget.value = ""
    this.customAmountInputTarget.parentElement.classList.remove("is-filled")

    this.lastSelectedAmount = null
  }

  selectPaymentType() {
    const formData = this.getFormData()

    this.paypalTarget.classList.remove("payment-paypal", "payment-card")

    this.paypalTarget.classList.add(`payment-${formData.paymentMethod}`)
    this.subscriptionInfoTarget.innerHTML = lang("donate_form")[`subscription_${formData.paymentMethod}_cancel_info`]
  }

  selectPaymentFrequency(e) {
    if (this.ppIsLoading) return

    const formData = this.getFormData()

    this.amountsTarget.classList.add("is-hidden")
    this.plansTarget.classList.add("is-hidden")

    if (formData.paymentFrequency === "monthly") {
      this.plansTarget.classList.remove("is-hidden")

      this.subscriptionInfoTarget.classList.remove("is-hidden")
      this.subscriptionInfoTarget.innerHTML = lang("donate_form")[`subscription_${formData.paymentMethod}_cancel_info`]
    } else {
      this.amountsTarget.classList.remove("is-hidden")
      this.subscriptionInfoTarget.classList.add("is-hidden")
    }

    this.ppInitButtons()
  }

  onPaymentSuccess(e) {
    trigger("snackbar:show", {
      message: lang("donate_form").payment_success,
      autoHide: true,
      status: "success",
    })

    this.formTarget.classList.add("is-hidden")
    this.messageTarget.classList.remove("is-hidden")

    window.scrollTo({
      top: offset(this.element).top - document.getElementById("MainNav").offsetHeight,
      behavior: "smooth",
    })
  }

  onPaymentError(e) {
    trigger("snackbar:show", {
      message: lang("donate_form").payment_error,
      autoHide: true,
      status: "error",
    })
  }

  // PayPal functions

  ppInitButtons() {
    if (this.ppIsLoading) return

    // cleanup first
    if (this.ppScriptNode) {
      this.ppScriptNode.removeEventListener("load", this.ppTriggerLoadEvent)
      this.ppScriptNode.remove()
    }
    this.paypalTarget.innerHTML = ""
    this.paypalTarget.classList.add("disabled")
    this.buttonTarget.classList.add("disabled")

    // do the thing
    const formData = this.getFormData()
    const disableFunding = [
      "bancontact",
      "blik",
      "eps",
      "giropay",
      "ideal",
      "mercadopago",
      "mybank",
      "p24",
      "sepa",
      "sofort",
      "venmo",
    ]

    this.ppScriptNode = document.createElement("script")
    this.ppScriptNode.async = true
    this.ppScriptNode.addEventListener("load", this.ppTriggerLoadEvent)

    const params = {
      "client-id": config.PAYPAL_CLIENT_ID,
      locale: this.ppLocale,
      intent: formData.paymentFrequency === "monthly" ? "subscription" : "capture",
      vault: formData.paymentFrequency === "monthly",
      "disable-funding": disableFunding.join(","),
      currency: formData.currency,
    }

    const queryString = Object.keys(params)
      .map((key) => `${key}=${params[key]}`)
      .join("&")

    this.ppScriptNode.src = `https://www.paypal.com/sdk/js?${queryString}`
    this.ppIsLoading = true

    document.body.append(this.ppScriptNode)
  }

  ppTriggerLoadEvent() {
    trigger("pp:scriptLoaded", null, document)
  }

  ppOnScriptLoad(e) {
    if (window.paypal) {
      window.donateController = this

      const formData = this.getFormData()
      const ppButtonParams = {
        style: {
          layout: "vertical",
          height: 48,
          shape: "rect",
          tagline: false,
        },
        onApprove: this.ppOnApprove,
        onCancel: this.ppOnCancel,
        onError: this.ppOnError,
      }

      if (formData.paymentFrequency === "monthly") {
        ppButtonParams.createSubscription = this.ppCreateSubscription
      } else {
        ppButtonParams.createOrder = this.ppCreateOrder

        // by adding this we force the card button to open the window instead of the inline form
        // see: https://github.com/paypal/paypal-checkout-components/issues/1224
        ppButtonParams.onShippingChange = function (data, actions) {
          // if not needed do nothing..
          return actions.resolve()
        }
      }

      window.paypal.Buttons(ppButtonParams).render(this.paypalTarget)

      setTimeout(() => {
        this.ppIsLoading = false
        this.selectPaymentType()
        this.validateForm()
      }, 30)
    }
  }

  ppCreateSubscription(data, actions) {
    if (window.donateController) {
      const formData = window.donateController.getFormData()

      return actions.subscription.create({
        plan_id: window.donateController.ppPlans[formData.currency][formData.plan_amount],
        application_context: {
          locale: window.donateController.ppLocale.replace("_", "-"),
        },
      })
    }
  }

  ppCreateOrder(data, actions) {
    if (window.donateController) {
      const formData = window.donateController.getFormData()

      return actions.order.create({
        purchase_units: [
          {
            amount: {
              value: formData.amount,
            },
          },
        ],
        application_context: {
          locale: window.donateController.ppLocale.replace("_", "-"),
        },
      })
    }
  }

  ppOnApprove(data, actions) {
    // for one time payments
    if (actions.order) {
      return actions.order.capture().then((details) => {
        if (details && details.status === "COMPLETED") {
          trigger("pp:approved", { data: details }, document)
        } else {
          trigger("pp:error", { data: details }, document)
        }
      })
    }

    // for subscriptions - thanks to this answer: https://www.paypal-community.com/t5/SDKs/Get-Subscription-Details/td-p/2213003
    if (actions.subscription) {
      if (data.subscriptionID) {
        return actions.subscription.get(data.subscriptionID).then((details) => {
          if (details && details.status === "ACTIVE") {
            trigger("pp:approved", { data: details }, document)
          } else {
            trigger("pp:error", { data: details }, document)
          }
        })
      } else {
        trigger("pp:error", { data: data }, document)
      }
    }
  }

  ppOnCancel(data) {
    trigger("pp:cancelled", { data: data }, document)
  }

  ppOnError(err) {
    trigger("pp:error", { data: err }, document)
  }
}
